import type { ScopeSourceRef } from '@protoss/shared';
import './source-quote-line.css';

export interface SourceQuoteLineProps {
  /**
   * The already-VERIFIED verbatim transcript quote carried by the payload (chat capture card
   * item.sourceQuote / persisted scope_meta.sourceQuote). Verification (whitespace-normalized
   * containment against the source chunks) happened server-side before the value was ever
   * stored - this component never receives an unverified quote and never infers one.
   */
  quote?: string | null;
  /**
   * Structured per-document provenance (scope_meta.sourceRefs): every verified quote tied to
   * the transcript document it came from. When present it supersedes `quote` (which equals
   * refs[0].quote by construction): one ref renders inline with its document name; several
   * render the document names with the quotes behind a native disclosure. Legacy items
   * without refs keep the single-quote rendering.
   */
  refs?: ScopeSourceRef[] | null;
}

/**
 * Muted provenance for an AI-discovered scope item: "From the transcript: <quote>", enriched
 * with the source document name(s) when the item carries structured sourceRefs.
 * Purely presentational; renders nothing when the payload carries no provenance.
 */
export function SourceQuoteLine({ quote, refs }: SourceQuoteLineProps) {
  const validRefs = (refs ?? []).filter((r) => r.quote?.trim());

  if (validRefs.length > 1) {
    const docTitles = [
      ...new Set(validRefs.map((r) => r.documentTitle?.trim()).filter(Boolean)),
    ] as string[];
    return (
      <details className="source-quote-line source-quote-line--multi">
        <summary className="source-quote-line__summary">
          <span className="source-quote-line__label">From the transcript:</span>{' '}
          <q className="source-quote-line__quote">{validRefs[0]?.quote.trim()}</q>{' '}
          <span className="source-quote-line__docs">
            (+{validRefs.length - 1} more{docTitles.length > 0 ? ` · ${docTitles.join(', ')}` : ''})
          </span>
        </summary>
        <ul className="source-quote-line__refs">
          {validRefs.map((r, i) => (
            <li key={i} className="source-quote-line__ref">
              <q className="source-quote-line__quote">{r.quote.trim()}</q>
              {r.documentTitle?.trim() ? (
                <span className="source-quote-line__docs"> - {r.documentTitle.trim()}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </details>
    );
  }

  const single = validRefs[0];
  const text = (single?.quote ?? (typeof quote === 'string' ? quote : ''))?.trim() ?? '';
  if (!text) return null;
  const docTitle = single?.documentTitle?.trim();
  return (
    <p className="source-quote-line">
      <span className="source-quote-line__label">From the transcript:</span>{' '}
      <q className="source-quote-line__quote">{text}</q>
      {docTitle ? <span className="source-quote-line__docs"> - {docTitle}</span> : null}
    </p>
  );
}
